/**
 * useUnreadAlertCount — the signed-in user's unread alert count.
 *
 * One query behind every place the number shows: the sidebar's Alerts count,
 * the Dashboard Alerts card pill, and the Alerts page's "N unread" header. It
 * counts the same rows the Alerts page lists as unread, so none of them can
 * disagree.
 *
 * Only runs when the Alerts module is on — an account without it never
 * requests the count once its modules have loaded.
 *
 * Anything that marks an alert read, or learns of a new one, invalidates
 * `['notifications']`, which refreshes this and the Dashboard card's rows.
 */
import { useQuery } from '@tanstack/react-query';
import { getUnreadCount } from '@/services/notifications.service';
import { useModuleAccess } from '@/hooks/useAccountModules';

export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const UNREAD_ALERT_COUNT_KEY = ['notifications', 'unread-count'] as const;

export function useUnreadAlertCount({ enabled = true }: { enabled?: boolean } = {}) {
  const { enabled: alertsModuleOn } = useModuleAccess('alerts');

  return useQuery({
    queryKey: UNREAD_ALERT_COUNT_KEY,
    queryFn: getUnreadCount,
    enabled: enabled && alertsModuleOn,
    staleTime: 60_000,
    retry: false,
  });
}
